// backend/seed/seedChapters.js and other imports remain unchanged
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useContext, useRef, createRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import ContentBlock from '../components/ContentBlock';
import './Chapter.css';
import logger from '../utils/logger';
import { isChapterLocked } from '../utils/courseLock';

// Accept setCurrentVisibleBlockText and setCurrentBlockIndex as props
function Chapter({ setCurrentVisibleBlockText, setCurrentBlockIndex, isAgentVisible = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  /* ------------------------------------------------------------------ */
  /* ⬇️  STATE & REFS                                                  */
  /* ------------------------------------------------------------------ */
  const { isAuthenticated, token, user } = useContext(AuthContext);
  const [chapter, setChapter] = useState(null);
  const [chapters, setChapters] = useState([]); // Add chapters list for navigation
  const [userAnswers, setUserAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [currentVisibleIndex, setCurrentVisibleIndex] = useState(0);
  const [chapterProgress, setChapterProgress] = useState({}); // Store chapter progress
  const [showCreateDeliverable, setShowCreateDeliverable] = useState(false); // True if any unlocked template includes this chapter
  const blockRefs = useRef([]);
  const containerRef = useRef(null);
  const trackRef     = useRef(null);
  const thumbRef     = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const positionUpdateTimerRef = useRef(null); // For debouncing position updates

  /* ------------------------------------------------------------------ */
  /* ⬇️  AUTH REDIRECT                                                 */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    // Wait for auth to be determined (not null), then redirect if not authenticated
    if (isAuthenticated === false) {
      // Save current location so we can redirect back after login
      navigate('/login', { 
        state: { from: location },
        replace: true 
      });
    }
  }, [isAuthenticated, navigate, location]);

  /* ------------------------------------------------------------------ */
  /* ⬇️  FETCH CHAPTER + USER PROGRESS                                 */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!isAuthenticated || !id) return;
    setIsLoading(true);

    (async () => {
      try {
        const [chapterRes, progressRes, chaptersRes, unitsRes, deliverablesRes] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/api/chapters/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${import.meta.env.VITE_API_URL}/api/user/progress`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${import.meta.env.VITE_API_URL}/api/chapters?list=1`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${import.meta.env.VITE_API_URL}/api/units`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${import.meta.env.VITE_API_URL}/api/deliverables/templates`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!chapterRes.ok || !progressRes.ok || !chaptersRes.ok || !unitsRes.ok) throw new Error('Network');

        const chapterData  = await chapterRes.json();
        const progressData = await progressRes.json();
        const chaptersData = await chaptersRes.json();
        const unitsData = await unitsRes.json();

        const progress = progressData || { progress: {}, completedChapters: [] };
        const userForLock = user || { role: 'student' };
        if (userForLock.role !== 'admin' && isChapterLocked(id, unitsData, chaptersData, progress, userForLock)) {
          navigate('/profile', { replace: true });
          return;
        }

        setChapter(chapterData);
        setChapters(chaptersData.sort((a, b) => a.order - b.order)); // Sort by order

        // Unlocked deliverables: show "Create deliverable" on last slide if any template includes this chapter
        const chapterIdStr = typeof id === 'object' ? id.toString() : String(id);
        if (deliverablesRes.ok) {
          const templates = await deliverablesRes.json().catch(() => []);
          const anyUnlockedIncludesThisChapter = (templates || []).some(
            t => (t.requiredChapterIds || []).map(String).includes(chapterIdStr)
          );
          setShowCreateDeliverable(anyUnlockedIncludesThisChapter);
        } else {
          setShowCreateDeliverable(false);
        }
        
        // Transform progress data: new structure is progress[chapterId].answers[contentBlockIndex]
        // We need to convert it to userAnswers[contentBlockIndex] for this chapter
        const chapterProgress = progressData.progress?.[chapterIdStr] || progressData.progress?.[id] || {};
        setChapterProgress(chapterProgress); // Store chapter progress for passing to ContentBlock
        const answersMap = {};
        
        // Handle new structure where answers[contentBlockIndex] is an object with {answer, in_review}
        // or old structure where it's just the answer value
        if (chapterProgress.answers) {
          Object.keys(chapterProgress.answers).forEach(contentBlockIndexStr => {
            const answerData = chapterProgress.answers[contentBlockIndexStr];
            // Convert string key to number for consistent lookup (Object.keys returns strings)
            const contentBlockIndex = parseInt(contentBlockIndexStr, 10);
            // Check if it's the new structure (object with answer and in_review) or old (just value)
            if (answerData && typeof answerData === 'object' && 'answer' in answerData) {
              answersMap[contentBlockIndex] = answerData.answer;
            } else {
              // Old structure - just the answer value
              answersMap[contentBlockIndex] = answerData;
            }
          });
        }
        
        // Debug: Log answers to verify they're loaded
        if (import.meta.env.DEV) {
          logger.debug('[Chapter] Answers loaded from backend:', answersMap);
          logger.debug('[Chapter] Chapter progress:', chapterProgress);
          logger.debug('[Chapter] Chapter contentBlocks indices:', chapterData.contentBlocks?.map(b => ({ index: b.index, blockType: b.blockType })));
        }

        setUserAnswers(answersMap);
        
        // If chapter is completed and not already in review mode, enter review mode
        const isCompleted = chapterProgress.status === 'completed';
        if (isCompleted) {
          // Check if any answer has in_review flag set
          const hasInReviewFlag = chapterProgress.answers && Object.values(chapterProgress.answers).some(
            answerData => answerData && typeof answerData === 'object' && answerData.in_review === true
          );
          
          if (!hasInReviewFlag) {
            // Enter review mode - set in_review: true on all answers
            try {
              await fetch(`${import.meta.env.VITE_API_URL}/api/user/enter-review-mode`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ chapterId: id }),
              });
              // Refetch progress to get updated in_review flags
              const updatedProgressRes = await fetch(`${import.meta.env.VITE_API_URL}/api/user/progress`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (updatedProgressRes.ok) {
                const updatedProgressData = await updatedProgressRes.json();
                const updatedChapterProgress = updatedProgressData.progress?.[chapterIdStr] || updatedProgressData.progress?.[id] || {};
                setChapterProgress(updatedChapterProgress);
                // Update answers map with new structure
                const updatedAnswersMap = {};
                if (updatedChapterProgress.answers) {
                  Object.keys(updatedChapterProgress.answers).forEach(contentBlockIndexStr => {
                    const answerData = updatedChapterProgress.answers[contentBlockIndexStr];
                    // Convert string key to number for consistent lookup
                    const contentBlockIndex = parseInt(contentBlockIndexStr, 10);
                    if (answerData && typeof answerData === 'object' && 'answer' in answerData) {
                      updatedAnswersMap[contentBlockIndex] = answerData.answer;
                    } else {
                      updatedAnswersMap[contentBlockIndex] = answerData;
                    }
                  });
                }
                setUserAnswers(updatedAnswersMap);
              }
            } catch (err) {
              console.error('Error entering review mode:', err);
            }
          }
        }
        
        if (chapterData && chapterData.contentBlocks) {
          blockRefs.current = Array(chapterData.contentBlocks.length + 1) // +1 for the last slide
            .fill()
            .map((_, i) => blockRefs.current[i] || createRef());
          
          // Always start at the beginning for review mode (completed chapters)
          // Resume from saved position only if chapter is in progress
          const isCompleted = chapterProgress.status === 'completed';
          
          // Check if any answer has in_review flag set (review mode)
          const hasInReviewFlag = chapterProgress.answers && Object.values(chapterProgress.answers).some(
            answerData => answerData && typeof answerData === 'object' && answerData.in_review === true
          );
          
          if (isCompleted && hasInReviewFlag) {
            // REVIEW MODE: Start at beginning (index 0) but show all previous answers
            setTimeout(() => {
              scrollToSlide(0);
              setCurrentVisibleIndex(0);
            }, 100);
          } else if (!isCompleted && chapterProgress.currentBlockIndex !== undefined && chapterProgress.currentBlockIndex >= 0) {
            // RESUME MODE: Go to last saved position (currentBlockIndex)
            // Find the array index that corresponds to the block.index stored in currentBlockIndex
            // currentBlockIndex stores block.index (from database), not array index
            const blockIndexToResume = chapterProgress.currentBlockIndex;
            const resumeArrayIndex = chapterData.contentBlocks.findIndex(
              block => block.index === blockIndexToResume
            );
            
            // If block not found by index, fall back to using currentBlockIndex as array index
            const resumeIndex = resumeArrayIndex !== -1 
              ? resumeArrayIndex 
              : Math.min(Math.max(0, blockIndexToResume), chapterData.contentBlocks.length - 1);
            
            // Small delay to ensure refs are set up
            setTimeout(() => {
              scrollToSlide(resumeIndex);
              setCurrentVisibleIndex(resumeIndex);
            }, 100);
          } else {
            // NEW CHAPTER or COMPLETED but not in review: Start at beginning
            setTimeout(() => {
              scrollToSlide(0);
              setCurrentVisibleIndex(0);
            }, 100);
          }
        }
      } catch (err) {
        console.error(err);
        setChapter(null);
      } finally {
        setIsLoading(false);
      }
    })();
    
    // Cleanup: Clear the block text and block index when chapter ID changes or component unmounts
    return () => {
      if (setCurrentVisibleBlockText) {
        setCurrentVisibleBlockText('');
      }
      if (setCurrentBlockIndex) {
        setCurrentBlockIndex(null);
      }
      // Clear position update timer on unmount
      if (positionUpdateTimerRef.current) {
        clearTimeout(positionUpdateTimerRef.current);
      }
    };
  }, [id, isAuthenticated, token, user, setCurrentVisibleBlockText, setCurrentBlockIndex]);

  /* ------------------------------------------------------------------ */
  /* ⬇️  PREVENT BODY SCROLLING                                        */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => (document.body.style.overflow = original);
  }, []);

  /* ------------------------------------------------------------------ */
  /* ⬇️  KEY / WHEEL BLOCK INSIDE CONTAINER                            */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;

    const stopWheel = (e) => e.preventDefault();
    const stopKeys  = (e) => {
      const bad = ['ArrowUp','ArrowDown','PageUp','PageDown','Home','End',' '];
      const tag = e.target.tagName.toLowerCase();
      if (bad.includes(e.key) && tag !== 'input' && tag !== 'textarea' && !e.target.isContentEditable) e.preventDefault();
    };

    c.addEventListener('wheel', stopWheel, { passive:false });
    c.addEventListener('keydown', stopKeys);
    // Only set tabindex if container is visible (not hidden)
    if (c.offsetParent !== null) {
      c.setAttribute('tabindex','-1');
    }

    return () => {
      c.removeEventListener('wheel', stopWheel);
      c.removeEventListener('keydown', stopKeys);
      c.removeAttribute('tabindex');
    };
  }, [id]);

  /* ------------------------------------------------------------------ */
  /* ⬇️  DISCRETE SCROLLBAR DRAG LOGIC                                 */
  /* ------------------------------------------------------------------ */
  const totalBlocks = chapter?.contentBlocks?.length ?? 0; // Number of actual content blocks
  const totalSlides = totalBlocks + 1; // Total slides including the "lastSlide"

  const scrollToSlide = (idx) => {
    blockRefs.current[idx]?.current?.scrollIntoView({ behavior:'auto', block:'start' });
  };

  /** convert pointer position -> slide index */
  const positionToIndex = (clientY) => {
    const track = trackRef.current;
    if (!track) return 0;
    const { top, height } = track.getBoundingClientRect();
    const y   = Math.min(Math.max(clientY - top, 0), height);
    const pct = y / height;
    return Math.round(pct * (totalSlides - 1));
  };

  const handleTrackClick = (e) => scrollToSlide(positionToIndex(e.clientY));

  const handlePointerDown = (e) => {
    setIsDragging(true);
    scrollToSlide(positionToIndex(e.clientY));
  };

  useEffect(() => {
    if (!isDragging) return;
    const move = (e) => scrollToSlide(positionToIndex(e.clientY));
    const up   = () => setIsDragging(false);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [isDragging]);

  /* ------------------------------------------------------------------ */
  /* ⬇️  ANSWER HANDLER & VISIBILITY CALLBACK                          */
  /* ------------------------------------------------------------------ */
  // Updated to use contentBlockIndex instead of blockId
  const updateAnswer = async (contentBlockIndex, ans) => {
    // Ensure contentBlockIndex is a number
    const blockIndexNum = typeof contentBlockIndex === 'number' ? contentBlockIndex : parseInt(contentBlockIndex, 10);

    // Update local state immediately for UI responsiveness
    setUserAnswers((p) => {
      const updated = { ...p, [blockIndexNum]: ans };
      return updated;
    });
    
    // Auto-save progress when answer is updated
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/submit-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          chapterId: id,
          contentBlockIndex: blockIndexNum,
          answer: ans, // This should be the full table object for exercise blocks
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        // Error logged without sensitive data
        throw new Error(errorData.error || 'Failed to save answer');
      }
      
      const responseData = await response.json();
      // Update chapter progress with latest status from server
      if (responseData.progress) {
        setChapterProgress(prev => ({
          ...prev,
          status: responseData.progress.status,
          completionPercentage: responseData.progress.completionPercentage,
          currentBlockIndex: responseData.progress.currentBlockIndex
        }));
      }
    } catch (err) {
      console.error('[updateAnswer] Error auto-saving progress:', err);
    }
  };
  
  const handleInView = (idx) => {
    setCurrentVisibleIndex(idx);
    
    // Update currentBlockIndex when user navigates (forward or backward)
    // Use block.index (from database) not array index to match what's stored
    if (chapter && chapter.contentBlocks && idx < totalBlocks && token && id) {
      const currentBlock = chapter.contentBlocks[idx];
      const blockIndex = currentBlock?.index !== undefined ? currentBlock.index : idx;
      
      // Only update if different from current position
      if (chapterProgress.currentBlockIndex !== blockIndex) {
        // Clear any pending position update
        if (positionUpdateTimerRef.current) {
          clearTimeout(positionUpdateTimerRef.current);
        }
        
        // Debounce position updates to avoid too many API calls (1 second delay)
        positionUpdateTimerRef.current = setTimeout(async () => {
          try {
            await fetch(`${import.meta.env.VITE_API_URL}/api/user/update-position`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                chapterId: id,
                contentBlockIndex: blockIndex,
              }),
            });
            // Update local state
            setChapterProgress(prev => ({
              ...prev,
              currentBlockIndex: blockIndex
            }));
          } catch (err) {
            console.error('Error updating position:', err);
          }
        }, 1000);
      }
    }

    if (!setCurrentVisibleBlockText) return;

    if (chapter && chapter.contentBlocks && idx < totalBlocks) { // idx is within actual contentBlocks
      const currentBlock = chapter.contentBlocks[idx];
      const blockIndex = currentBlock?.index !== undefined ? currentBlock.index : idx;
      
      // Update currentBlockIndex for MakerstateAgent
      if (setCurrentBlockIndex) {
        setCurrentBlockIndex(blockIndex);
      }
      
      let blockText = '';
      // Updated to use new schema: blockType instead of type, content instead of data
      if (currentBlock.blockType === 'text') {
        blockText = currentBlock.content?.text || "";
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = blockText;
        blockText = (tempDiv.textContent || tempDiv.innerText || "").trim();
      } else if (currentBlock.blockType === 'question' && currentBlock.content?.prompt) {
        blockText = currentBlock.content.prompt.trim();
      } else if (currentBlock.blockType === 'exercise' && currentBlock.content) {
        // Exercise blocks can contain tables
        if (currentBlock.content.headings && Array.isArray(currentBlock.content.headings) && currentBlock.content.headings.length > 0) {
          blockText = `User is viewing a table with columns: ${currentBlock.content.headings.join(', ')}.`;
        } else {
          blockText = "User is viewing an exercise block.";
        }
      } else if (currentBlock.blockType === 'image' && currentBlock.content) {
        blockText = `User is viewing an image.`;
      } else if (currentBlock.type === 'ai' && currentBlock.data && currentBlock.data.welcomeMessage) {
        blockText = `User is interacting with an AI chat block. Welcome: "${currentBlock.data.welcomeMessage}"`;
      }
      setCurrentVisibleBlockText(blockText);
    } else if (idx === totalBlocks) { // This is the "lastSlide"
        if (setCurrentBlockIndex) {
          setCurrentBlockIndex(null); // No block index for the final summary slide
        }
        setCurrentVisibleBlockText("User is viewing the final summary slide of the chapter.");
    } else {
      if (setCurrentBlockIndex) {
        setCurrentBlockIndex(null);
      }
      setCurrentVisibleBlockText(''); 
    }
  };

  /* ------------------------------------------------------------------ */
  /* ⬇️  RENDER STATES                                                */
  /* ------------------------------------------------------------------ */
  if (isLoading || isAuthenticated === null) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-white dark:bg-gray-900">
        <div className="w-80 mb-4">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-black rounded-full transition-all duration-1000 ease-out"
              style={{
                width: '85%',
                animation: 'loadingProgress 0.25s ease-out forwards'
              }}
            />
          </div>
        </div>
        <style jsx>{`
          @keyframes loadingProgress {
            0% { width: 0% }
            50% { width: 60% }
            80% { width: 85% }
            100% { width: 95% }
          }
        `}</style>
      </div>
    );
  }
  if (!chapter)                               
    return <div className="flex flex-col items-center h-screen justify-center">Error loading.</div>;

  // Calculate unanswered question indexes for the last slide
  const unansweredIndexes = [];
  if (chapter && chapter.contentBlocks) {
    chapter.contentBlocks.forEach((block, index) => {
      // Updated to use blockType and index instead of type and _id
      if (block.blockType === 'question' || block.blockType === 'multi_choice') {
        const answer = userAnswers[block.index]; // Use block.index instead of block._id
        // Consider a question unanswered if there's no answer, or if the answer is an empty string (common for text inputs)
        // You might need to adjust this condition based on how your QuestionComponent stores empty/default answers for various question types
        if (answer === undefined || answer === null || (typeof answer === 'string' && answer.trim() === '') || (Array.isArray(answer) && answer.length === 0)) {
          unansweredIndexes.push(index + 1); // Store 1-based index for display
        }
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* ⬇️  UI                                                           */
  /* ------------------------------------------------------------------ */
  return (
    <div ref={containerRef} className="chapter-container h-screen w-full min-w-0 overflow-auto relative bg-white dark:bg-gray-900">
      {chapter && chapter.contentBlocks && chapter.contentBlocks.map((block, i) => (
        <ContentBlock
          key={block.index !== undefined ? block.index : i} // Use block.index instead of block._id
          blockRef={blockRefs.current[i]}
          block={block}
          index={i}
          chapterId={chapter._id}
          userAnswers={userAnswers}
          totalBlocks={totalSlides}
          updateAnswer={updateAnswer}
          onInView={handleInView}
          chapters={chapters}
          currentChapter={chapter}
          chapterProgress={chapterProgress}
        />
      ))}
      {/* last slide */}
      {chapter && (
          <ContentBlock
            key="last"
            blockRef={blockRefs.current[totalBlocks]} // Ref for the last slide
            block={{
              blockType: 'lastSlide',
              adjustedUnansweredIndexes: unansweredIndexes 
            }}
            index={totalBlocks} // Index for the last slide
            chapterId={chapter._id}
            userAnswers={userAnswers}
            totalBlocks={totalSlides}
            chapters={chapters} // Pass chapters for navigation
            currentChapter={chapter} // Pass current chapter
            chapterProgress={chapterProgress} // Pass chapter progress for review mode check
            onInView={handleInView} // Ensure this is called for the last slide too
            showCreateDeliverable={showCreateDeliverable}
            hasFeedbackRelevantContent={chapter.contentBlocks?.some(b => b.blockType === 'question' || b.blockType === 'multi_choice' || b.blockType === 'exercise') ?? false}
          />
      )}

        <div className={`fixed bottom-4 z-20 flex justify-center gap-4 left-0 ${isAgentVisible ? 'right-[33.3333%]' : 'right-0'}`}>
          <button 
            onClick={() => navigate('/profile')} 
            className="px-4 py-2 bg-gray-500 dark:bg-gray-600 text-white rounded disabled:opacity-40 hover:bg-gray-600 dark:hover:bg-gray-700"
            title="Back to Home"
          >
           Home
          </button>
          <button onClick={()=>scrollToSlide(Math.max(0, currentVisibleIndex-1))} disabled={currentVisibleIndex===0} className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded disabled:opacity-40">← Back</button>
          <button onClick={()=>scrollToSlide(Math.min(totalSlides-1, currentVisibleIndex+1))} disabled={currentVisibleIndex===totalSlides-1} className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded disabled:opacity-40">Next →</button>
        </div>

        {/* slide counter */}
      <div className="fixed font-body left-4 top-[calc(50%+12.5rem)] text-gray-600 dark:text-gray-400">{currentVisibleIndex+1}/{totalSlides}</div>

      {/* discrete scrollbar */}
      <div
        ref={trackRef}
        onClick={handleTrackClick}
        className="fixed left-[28px] top-1/2 -translate-y-1/2 h-96 w-2 bg-gray-300 dark:bg-gray-700 cursor-pointer z-[90]"
      >
        {/* Filled portion of the scrollbar */}
        <div
          className="absolute top-0 left-0 w-full bg-black dark:bg-white"
          style={{
            height: totalSlides > 1 ? `${(currentVisibleIndex / (totalSlides - 1)) * 100}%` : '0%',
          }}
        />
        <div
          ref={thumbRef}
          onPointerDown={handlePointerDown}
          className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-black dark:bg-white touch-none"
          style={{ top: totalSlides > 1 ? `${(currentVisibleIndex / (totalSlides - 1)) * 100}%` : '0%' }}
        />
      </div>
    </div>
  );
}

export default Chapter;
